// LegacyLayer Theme System - Centralized Styling
// This file contains all theme constants and styling classes
// Always reference this file for consistent styling across the app

export type Theme = 'light' | 'dark';

export const themes = {
  light: {
    // Color Palette
    colors: {
      // Primary Light Theme
      background: {
        main: 'bg-gradient-to-br from-legacy-warm-white to-legacy-off-white',
        card: 'bg-white',
        cardHover: 'hover:bg-gray-50',
        transparent: 'bg-transparent',
      },
      
      // Text Colors
      text: {
        primary: 'text-gray-900',
        secondary: 'text-gray-600',
        muted: 'text-gray-500',
        accent: 'text-accent-wood',
      },
      
      // Border Colors
      border: {
        primary: 'border-gray-200',
        secondary: 'border-gray-300',
        accent: 'border-accent-wood/30',
      },
      
      // Accent Colors
      accent: {
        wood: 'text-accent-wood',
        woodBg: 'bg-accent-wood',
        woodBgLight: 'bg-accent-wood-light',
        woodBgHover: 'hover:bg-accent-wood-light',
        woodBgTransparent: 'bg-accent-wood/10',
      },
    },
    
    // Navigation
    nav: {
      container: 'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      scrolled: 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200',
      transparent: 'bg-transparent',
      link: 'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 group',
      linkActive: 'text-accent-wood bg-accent-wood/10 border border-accent-wood/20',
      linkInactive: 'text-gray-600 hover:text-accent-wood hover:bg-gray-100',
    },
    
    // Cards
    card: {
      primary: 'bg-white rounded-2xl shadow-lg border border-gray-200 p-8',
      accent: 'bg-gradient-to-br from-accent-wood/5 to-accent-wood/10 rounded-2xl border border-accent-wood/20 p-8',
      hover: 'hover:shadow-xl transition-shadow',
    },
    
    // Buttons
    button: {
      primary: 'bg-accent-wood hover:bg-accent-wood-light text-white px-6 py-3 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg flex items-center gap-3',
      secondary: 'bg-gray-100 text-accent-wood px-3 py-1 rounded-full text-sm',
    },
    
    // Icons
    icon: {
      container: 'w-12 h-12 bg-accent-wood/10 rounded-lg flex items-center justify-center',
      large: 'w-16 h-16 bg-accent-wood/10 rounded-xl flex items-center justify-center',
      hero: 'w-20 h-20 bg-accent-wood/10 rounded-2xl flex items-center justify-center mx-auto mb-6',
    },
  },
  
  dark: {
    // Color Palette
    colors: {
      // Primary Dark Theme
      background: {
        main: 'bg-gradient-to-br from-primary-900 to-primary-800',
        card: 'bg-primary-800',
        cardHover: 'hover:bg-primary-700',
        transparent: 'bg-transparent',
      },
      
      // Text Colors
      text: {
        primary: 'text-white',
        secondary: 'text-primary-300',
        muted: 'text-primary-400',
        accent: 'text-accent-wood',
      },
      
      // Border Colors
      border: {
        primary: 'border-primary-700',
        secondary: 'border-primary-600',
        accent: 'border-accent-wood/30',
      },
      
      // Accent Colors
      accent: {
        wood: 'text-accent-wood',
        woodBg: 'bg-accent-wood',
        woodBgLight: 'bg-accent-wood-light',
        woodBgHover: 'hover:bg-accent-wood-light',
        woodBgTransparent: 'bg-accent-wood/20',
      },
    },
    
    // Navigation
    nav: {
      container: 'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
      scrolled: 'bg-primary-900/95 backdrop-blur-md shadow-lg border-b border-primary-700',
      transparent: 'bg-transparent',
      link: 'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 group',
      linkActive: 'text-accent-wood bg-accent-wood/20 border border-accent-wood/30',
      linkInactive: 'text-primary-300 hover:text-accent-wood hover:bg-primary-700',
    },
    
    // Cards
    card: {
      primary: 'bg-primary-800 rounded-2xl shadow-lg border border-primary-700 p-8',
      accent: 'bg-gradient-to-br from-accent-wood/10 to-accent-wood/20 rounded-2xl border border-accent-wood/30 p-8',
      hover: 'hover:shadow-xl transition-shadow',
    },
    
    // Buttons
    button: {
      primary: 'bg-accent-wood hover:bg-accent-wood-light text-white px-6 py-3 rounded-xl font-semibold text-lg transition-all duration-300 shadow-lg flex items-center gap-3',
      secondary: 'bg-primary-700 text-accent-wood px-3 py-1 rounded-full text-sm',
    },
    
    // Icons
    icon: {
      container: 'w-12 h-12 bg-accent-wood/20 rounded-lg flex items-center justify-center',
      large: 'w-16 h-16 bg-accent-wood/20 rounded-xl flex items-center justify-center',
      hero: 'w-20 h-20 bg-accent-wood/20 rounded-2xl flex items-center justify-center mx-auto mb-6',
    },
    },
};

// Layout Classes
export const layout = {
  container: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
  section: 'py-8',
  card: 'rounded-2xl shadow-lg border p-8',
  cardHover: 'hover:shadow-xl transition-shadow',
};

// Typography Classes
export const typography = {
  h1: 'text-4xl md:text-6xl font-display font-bold',
  h2: 'text-3xl font-display font-bold',
  h3: 'text-2xl font-display font-bold',
  body: 'text-lg',
  bodySmall: 'text-sm',
  mono: 'font-mono',
};

// Animation Classes
export const animation = {
  fadeIn: 'animate-fade-in',
  slideUp: 'animate-slide-up',
  pulseSlow: 'animate-pulse-slow',
};

// Spacing
export const spacing = {
  section: 'mb-16',
  card: 'mb-12',
  grid: 'gap-6',
  flex: 'space-x-2',
};

// Theme-aware class combinations
export const getClassNames = (theme: Theme) => {
  const themeConfig = themes[theme];
  
  const classNames = {
    // Page Layout
    pageContainer: 'min-h-screen py-8',
    pageContent: 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
    
    // Hero Sections
    heroSection: 'text-center mb-16',
    heroTitle: `text-4xl md:text-6xl font-display font-bold ${themeConfig.colors.text.primary} mb-6`,
    heroSubtitle: `text-xl ${themeConfig.colors.text.secondary} max-w-3xl mx-auto`,
    
    // Content Sections
    contentSection: 'space-y-12',
    sectionTitle: `text-3xl font-display font-bold ${themeConfig.colors.text.primary} mb-4`,
    sectionText: `${themeConfig.colors.text.secondary} text-lg`,
    
    // Cards
    cardPrimary: `${themeConfig.card.primary}`,
    cardAccent: `${themeConfig.card.accent}`,
    cardHover: 'hover:shadow-xl transition-shadow',
    
    // Grid Layouts
    gridCols: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6',
    gridCols4: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6',
    
    // Navigation
    nav: {
      container: themeConfig.nav.container,
      scrolled: themeConfig.nav.scrolled,
      transparent: themeConfig.nav.transparent,
      link: themeConfig.nav.link,
      linkActive: themeConfig.nav.linkActive,
      linkInactive: themeConfig.nav.linkInactive,
    },
    
    // Buttons
    buttonPrimary: themeConfig.button.primary,
    buttonSecondary: themeConfig.button.secondary,
    
    // Text
    textPrimary: themeConfig.colors.text.primary,
    textSecondary: themeConfig.colors.text.secondary,
    textMuted: themeConfig.colors.text.muted,
    textAccent: themeConfig.colors.text.accent,
    
    // Icons
    icon: {
      container: themeConfig.icon.container,
      large: themeConfig.icon.large,
      hero: themeConfig.icon.hero,
    },
    
    // Background
    background: themeConfig.colors.background.main,
  };
  
  return classNames;
};

// Helper function to combine classes
export const cn = (...classes: (string | undefined | null | false)[]) => {
  return classes.filter(Boolean).join(' ');
};

// Usage example:
// import { classNames, cn } from '@/styles/theme';
// 
// // Simple usage
// <div className={classNames.cardPrimary}>
// 
// // Combined usage
// <div className={cn(classNames.cardPrimary, classNames.cardHover)}> 